// Client 360 on the phone (CLIENT-360-SPEC, companion port 2026-08-09 — "FULL 360 in the v1 DoD").
// Rendered as the 360 tab inside the contact detail (#ctDetail): auto-recap → KPI chips →
// per-module sections (rows = read, "all →" = the module pre-searched, "+" = CREATE pre-scoped —
// only where the phone actually has a create surface: Task · Note · Receipt; Docs/Quotes are
// read-only on mobile, tickets always) → History timeline (+ Show more) → Ask Brain.
// Data: /api/c360 (companion proxy → Outlook backend). SWR per fiche via loadSnap/saveSnap;
// the recap refreshes ONLY when the timeline head moved (zero LLM calls on an unchanged fiche).
import { loadSnap, saveSnap, skeletonHtml } from "./cache";
import { fmtDate, fmtMoney } from "./locale";
import { showScreen, type Screen } from "./app";
import { askConnect, hasCrmConnector } from "./connect";
import { loadTasks, openTaskFor } from "./tasks";
import { loadMinutes, openMinuteFor } from "./minutes";
import { loadLedger, captureReceiptFor } from "./receipts";
import { loadDocs } from "./docs";
import { loadQuotes } from "./quotes";

const $ = <T extends Element = HTMLElement>(s: string): T | null => document.querySelector<T>(s);
const esc = (s: unknown): string => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

// The subject of a 360: a CONTACT (id + email set) or a COMPANY (domain-only — id/email null).
// The scope decides which "+" creators render: task needs a contact id, note needs an attendee
// email, receipt works for both (vendor identity).
export type C360Contact = { id: number | string | null; name: string | null; email: string | null; company: string | null; domain: string | null };

type Chips = { tasks: number; receipts: number; unpaid_cents: number; docs: number; notes: number; quotes: number; tickets: number | null };
type TlItem = { module: string; kind: string; key: string; title: string; meta: string | null; actor: string | null; at: string };
type C360 = {
  chips: Chips;
  sections: {
    tasks: Array<{ id: number; title: string; status: string; due_date: string | null }>;
    receipts: Array<{ id: number; vendor_name: string | null; doc_ref: string | null; currency: string | null; amount_total: string | null; paid: boolean }>;
    docs: Array<{ id: number; name: string; first_seen_at: string }>;
    notes: Array<{ id: number; title: string; meeting_date: string | null }>;
    quotes: Array<{ id: number; ref: string; status: string; stage: string | null; total_excl_cents: string | null; visitors: number; last_viewed_at: string | null }>;
    tickets: Array<{ id: number; ref: string; subject: string; stage: string; updated_at: string }> | null;
  };
  flags: { quotes: boolean; tickets: boolean };
  timeline?: { items: TlItem[]; next: { at: string; key: string } | null; head: string | null };
  recap?: { text: string; head: string } | null;
  web?: WebRes | null;
};
// §9.3 — mirrors taskpane/src/api/c360.ts. `verdict` is two-valued on the wire; a third state
// (an independent signal DISAGREED) never leaves the server.
type WebProfile = {
  kind: "person" | "company";
  fields: Record<string, string | undefined>;
  verdict: "verified" | "unverified";
  verifiedBy: string | null;
  sources: Array<{ title: string; url: string }>;
  at: string;
};
// On a CONTACT fiche both lanes run: `web` is the person, `company` is the firm they work for.
// On a company fiche only `web` is set, and it is the company.
type WebRes = {
  web: WebProfile | null; skipped?: string; cached?: boolean;
  company?: WebProfile | null; companySkipped?: string;
};
// §9.4 — vendor-neutral Customer360 (netlify/lib/crm360/types.mts). Exact maps onto these fields.
type CrmRes = {
  crm: {
    kpis: { open_total: number; overdue_total: number; revenue_12m: number; quotes_count: number; opps_count: number };
    open_invoices: Array<{ name: string; due: string; residual: number; overdue: boolean }>;
    quotations: Array<{ name: string; date: string; total: number }>;
    opportunities: Array<{ name: string; stage: string; expected: number }>;
    sections_omitted: string[];
  } | null;
  vendor?: string;
  label?: string;
  skipped?: string;
};

// per-open state (the detail is rebuilt per contact)
let cur: C360Contact | null = null;
let data: C360 | null = null;
let tlMore: TlItem[] = [];
let tlNext: { at: string; key: string } | null = null;
let tlBusy = false;
let refreshing = false;              // SWR revalidate in flight over a cached paint (fuzzy pulse)
let tlOpenG = new Set<string>();     // expanded History groups (collapsed by default)
let recapBusy = false;
let onNavigate: (() => void) | null = null; // supplied by contacts.ts — closes the detail overlay
let hostEl: HTMLElement | null = null;      // the tab container this open renders into
// §9.3 web identity strip: its own state and its own request, so a 2–5 s web call never delays
// the own-data 360. Bought once per subject server-side; "refresh" is the explicit re-buy.
let web: WebRes | null = null;
let webBusy = false;
// §9.4 CRM lane: on demand, never cached — an invoice paid this morning must not come from a
// snapshot. skipped === "no_connector" hides the block entirely (org without an ERP).
let crm: CrmRes | null = null;
let crmBusy = false;
// Deep Scan (MEETING-SCAN-BRAINSTORM, Gery 2026-08-18): one tap → the full dossier by mail.
let scanState: "idle" | "queued" | "error" = "idle";

const snapKey = (c: C360Contact): string => `c360:${(c.email || c.domain || "").toLowerCase()}`;
const qs = (c: C360Contact): string => {
  const p = new URLSearchParams();
  if (c.id != null) p.set("contact_id", String(c.id));
  if (c.email) p.set("email", c.email.toLowerCase());
  if (c.domain) p.set("domain", c.domain.toLowerCase());
  if (c.company) p.set("label", c.company);
  return p.toString();
};

export function renderC360(host: HTMLElement, c: C360Contact, opts: { onNavigate: () => void }): void {
  cur = c;
  onNavigate = opts.onNavigate;
  hostEl = host;
  tlMore = []; tlNext = null; tlBusy = false; recapBusy = false; tlOpenG = new Set(); refreshing = false;
  data = loadSnap<C360>(snapKey(c));
  web = data?.web ?? null; webBusy = false;
  crm = null; crmBusy = false;
  scanState = "idle";
  paint(host);
  void refresh(host);
  if (!web) void refreshWeb(host, false); // already known for this fiche → no call at all
  host.onclick = onClick; // one delegated handler per open (host is rebuilt per contact)
}

/** §9.4 — fetch the ERP answer and render it in place (never on paint, never cached). */
async function refreshCrm(host: HTMLElement): Promise<void> {
  if (!cur || crmBusy) return;
  crmBusy = true; paint(host);
  try {
    const who = cur.name || cur.company || cur.domain || cur.email || "";
    const r = await fetch(`/api/c360?${qs(cur)}&mode=crm&who=${encodeURIComponent(who)}`, { credentials: "include" });
    crm = (await r.json().catch(() => null)) as CrmRes | null;
  } catch {
    crm = { crm: null, skipped: "error" };
  }
  crmBusy = false;
  paint(host);
}

/** §9.3 — the identity strip, on its own request so the own-data 360 paints first. */
async function refreshWeb(host: HTMLElement, force: boolean): Promise<void> {
  if (!cur || webBusy) return;
  webBusy = true; paint(host);
  try {
    const r = await fetch(`/api/c360?${qs(cur)}&mode=web${force ? "&refresh=1" : ""}`, { credentials: "include" });
    web = (await r.json().catch(() => null)) as WebRes | null;
  } catch {
    web = { web: null, skipped: "unavailable" };
  }
  webBusy = false;
  // Ride the fiche snapshot so a re-opened contact paints the strip without a round-trip.
  if (cur) { const snap = loadSnap<C360>(snapKey(cur)); if (snap) { snap.web = web; saveSnap(snapKey(cur), snap); } if (data) data.web = web; }
  paint(host);
}

async function refresh(host: HTMLElement): Promise<void> {
  if (!cur) return;
  // Gery live-test round 2: the silent cache→fresh swap read as "jumping" — pulse the tab
  // soft-fuzzy while the revalidate is in flight, settle when the fresh data lands.
  if (data) { refreshing = true; paint(host); }
  try {
    const r = await fetch(`/api/c360?${qs(cur)}`, { credentials: "include" });
    refreshing = false;
    if (!r.ok) { paint(host, !data ? (r.status === 402 ? "Client 360 is part of the subscription." : "Couldn't load the 360 view.") : undefined); return; }
    const j = (await r.json()) as C360;
    const prev = loadSnap<C360>(snapKey(cur));
    if (prev?.recap?.text) j.recap = prev.recap; // recap is minted separately — carry it forward (never an empty one)
    data = j; tlNext = j.timeline?.next ?? null;
    saveSnap(snapKey(cur), j);
    paint(host);
    // §8c head-gate: a new recap ONLY when the timeline head moved since the cached one.
    const head = j.timeline?.head ?? null;
    if (head && j.recap?.head !== head && !recapBusy) {
      recapBusy = true; paint(host);
      try {
        const rr = await fetch(`/api/c360?${qs(cur)}&mode=recap&who=${encodeURIComponent(cur.name || "")}`, { credentials: "include" });
        const jj = (await rr.json().catch(() => ({}))) as { recap?: { text: string; head: string } | null };
        if (rr.ok && jj.recap?.text && data) {
          data.recap = jj.recap;
          const snap = loadSnap<C360>(snapKey(cur));
          if (snap) { snap.recap = jj.recap; saveSnap(snapKey(cur), snap); }
        }
      } catch { /* recap is a bonus — never break the fiche */ }
      recapBusy = false; paint(host);
    }
  } catch { refreshing = false; paint(host, !data ? "Couldn't load the 360 view — check your connection." : undefined); }
}

// ── render ────────────────────────────────────────────────────────────────────
const SEC_TITLES: Record<string, string> = { tasks: "Tasks", quotes: "Quotes", receipts: "Receipts", tickets: "Tickets", notes: "Notes", docs: "Docs" };
const MOD_LABELS: Record<string, string> = { task: "Task", note: "Note", receipt: "Receipt", doc: "Doc", quote: "Quote", ticket: "Ticket" };
const KIND_SUFFIX: Record<string, string> = { sent: "sent", saved: "saved", downloaded: "downloaded", note: "note", viewed: "viewed" };

// §9.2/§9.3 — the web identity strip sits ABOVE the recap: the recap says what is RUNNING, this
// says WHO this is. Phone has no per-field "Use" (there is no contact FORM on this tab — the
// desktop Contact tab owns that), so it is read-only here, by design and not by omission.
// Two different reasons, two different actions — "add a company OR a work e-mail" was wrong: a
// work address alone is not enough, the firm's NAME is what the search needs (law 1).
const SKIP_COPY: Record<string, string> = {
  no_context: "Not enough to go on — fill in this contact's Company on the Contact tab, then refresh.",
  no_company_name: "We don't know this firm's name yet. Open the Company tab to fetch its details (or type the name into the contact), then refresh.",
  freemail: "Personal e-mail address — there's no company behind it to look up.",
  no_result: "Nothing found on the web for this one.",
  rate_limited: "Daily enrichment limit reached — try again tomorrow.",
  mismatch: "Found a similar name, but it is someone else — nothing shown.",
};
const webHost = (u: string): string => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };
function webBlockHtml(title: string, p: WebProfile | null | undefined, skipped: string | undefined, withRefresh: boolean): string {
  // Refresh belongs on EVERY block a refresh could change — hiding it on the empty company block
  // is what made "refresh doesn't work" look true. Hidden only when it genuinely cannot help.
  const canRefresh = withRefresh && skipped !== "freemail";
  const hd = (inner: string): string =>
    `<div class="c3m-sec"><div class="c3m-sec-hd"><span class="c3m-lab">${esc(title)}</span><span class="sp"></span>` +
    (canRefresh ? `<button type="button" class="c3m-all" data-act="webrefresh"${webBusy ? " disabled" : ""}>${webBusy ? "…" : "refresh"}</button>` : "") +
    `</div>${inner}</div>`;
  if (webBusy && !p) return hd(skeletonHtml(2));
  if (!p) {
    const copy = skipped ? SKIP_COPY[skipped] : "";
    return copy ? hd(`<div class="c3m-webv">${esc(copy)}</div>`) : "";
  }
  const f = p.fields;
  // "About" is PROSE and wraps; every other row keeps the one-line ellipsis.
  const line = (label: string, v?: string): string =>
    v ? `<div class="c3m-row"><div class="c3m-main${label === "About" ? " wrap" : ""}">${esc(label === "LinkedIn" ? v.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") : v)}</div><div class="c3m-meta">${esc(label)}</div></div>` : "";
  const rows = p.kind === "person"
    ? line("Role", f.role) + line("Employer", f.employer) + line("Location", f.location) + line("LinkedIn", f.linkedin)
    : line("About", f.description) + line("Industry", f.industry) + line("Employees", f.employees) + line("Head office", f.headquarters) + line("Founded", f.founded) + line("Part of", f.parentGroup);
  // Grounding proves SOURCING, not identity — so never a bare "verified" badge: say what
  // confirmed it, or admit that nothing did.
  const verdict = p.verdict === "verified"
    ? `Confirmed via ${esc(p.verifiedBy || "an independent source")}`
    : "Unconfirmed — no independent signal matched, so check it before you rely on it.";
  const src = p.sources.slice(0, 2).map((s) => `<a href="${esc(s.url)}" target="_blank" rel="noopener">${esc(webHost(s.url))}</a>`).join(", ");
  return hd(rows + `<div class="c3m-webv">${verdict}${src ? ` · ${src}` : ""}</div>`);
}
/** A contact fiche shows BOTH: who this is, then who they work for (§9.2). */
function webStripHtml(): string {
  const isPerson = web?.web?.kind === "person" || (!!web?.company && !web?.web);
  const coName = (web?.company?.fields.name || "").trim();
  return webBlockHtml("On the web", web?.web, web?.skipped, true) +
    (isPerson ? webBlockHtml(coName ? `About ${coName}` : "About the company", web?.company, web?.companySkipped, true) : "");
}

// §9.4 — the ERP answer lands IN the fiche instead of throwing the user into a chat. Rendered
// only when a CRM connector can actually answer: no connector, no access or no adapter for that
// vendor → no block at all, because a hole where a feature would be is worse than no hole.
function crmSecHtml(): string {
  if (!hasCrmConnector() || crm?.skipped === "no_connector") return "";
  const hd = (inner: string, refresh = false): string =>
    `<div class="c3m-sec"><div class="c3m-sec-hd"><span class="c3m-lab">CRM${crm?.label ? ` · ${esc(crm.label)}` : ""}</span><span class="sp"></span>` +
    (refresh ? `<button type="button" class="c3m-all" data-act="crm"${crmBusy ? " disabled" : ""}>${crmBusy ? "…" : "refresh"}</button>` : "") +
    `</div>${inner}</div>`;
  if (crmBusy && !crm?.crm) return hd(skeletonHtml(3));
  const d = crm?.crm;
  if (!d) {
    if (!crm?.skipped) {
      return hd(`<div class="c3m-row go" data-act="crm"><div class="c3m-main">Load from CRM</div><div class="c3m-meta">Open quotes, orders and invoices from your ERP — fetched only when you ask</div></div>`);
    }
    const copy = crm.skipped === "timeout" ? "Your ERP did not answer in time — try again."
      : crm.skipped === "not_found" ? "This customer was not found in your ERP."
      : "Couldn't reach your ERP just now.";
    return hd(`<div class="c3m-webv">${esc(copy)}</div><div class="c3m-row go" data-act="crm"><div class="c3m-main">Try again</div></div>`);
  }
  const chips: string[] = [];
  if (d.kpis.overdue_total > 0) chips.push(`<span class="c3m-chip">${esc(fmtMoney(d.kpis.overdue_total))} overdue</span>`);
  if (d.kpis.open_total > 0) chips.push(`<span class="c3m-chip">${esc(fmtMoney(d.kpis.open_total))} open</span>`);
  if (d.kpis.revenue_12m > 0) chips.push(`<span class="c3m-chip">${esc(fmtMoney(d.kpis.revenue_12m))} last 12m</span>`);
  if (d.kpis.quotes_count > 0) chips.push(`<span class="c3m-chip">${d.kpis.quotes_count} quotation${d.kpis.quotes_count === 1 ? "" : "s"}</span>`);
  if (d.kpis.opps_count > 0) chips.push(`<span class="c3m-chip">${d.kpis.opps_count} opportunit${d.kpis.opps_count === 1 ? "y" : "ies"}</span>`);
  const rows =
    d.open_invoices.slice(0, 3).map((i) =>
      `<div class="c3m-row"><div class="c3m-main">${esc(i.name)}</div><div class="c3m-meta${i.overdue ? " warn" : ""}">${esc(fmtMoney(i.residual))} · ${i.overdue ? "overdue" : "due"} ${esc(fmtDate(i.due))}</div></div>`).join("") +
    d.quotations.slice(0, 2).map((q) =>
      `<div class="c3m-row"><div class="c3m-main">${esc(q.name)}</div><div class="c3m-meta">${esc(fmtMoney(q.total))} · ${esc(fmtDate(q.date))}</div></div>`).join("") +
    d.opportunities.slice(0, 2).map((o) =>
      `<div class="c3m-row"><div class="c3m-main">${esc(o.name)}</div><div class="c3m-meta">${esc(o.stage)} · ${esc(fmtMoney(o.expected))}</div></div>`).join("");
  // A narrow connector profile yields a SHORT report — name the withheld blocks, or the gap
  // reads as "this customer has none of those".
  const omitted = d.sections_omitted.length
    ? `<div class="c3m-webv">Not shown (your CRM profile has no access): ${esc(d.sections_omitted.join(", "))}.</div>` : "";
  return hd(
    (chips.length ? `<div class="c3m-chips">${chips.join("")}</div>` : "") + rows + omitted +
    `<div class="c3m-row go" data-act="crmbrain"><div class="c3m-main">Open in Brain</div><div class="c3m-meta">Ask follow-up questions about this ERP data</div></div>`,
    true,
  );
}

function paint(host: HTMLElement, err?: string): void {
  if (!cur) return;
  host.classList.toggle("c3m-refreshing", refreshing && !!data);
  if (!data) {
    host.innerHTML = err
      ? `<div class="c3m-empty">${esc(err)}</div>`
      : skeletonHtml(4);
    return;
  }
  const c = data.chips;
  const s = data.sections;
  const who = cur.name || cur.company || cur.email || cur.domain || "";

  const chips: string[] = [];
  if (c.tasks > 0) chips.push(chip(`${c.tasks} open task${c.tasks === 1 ? "" : "s"}`, "all:tasks"));
  if (c.quotes > 0) chips.push(chip(`${c.quotes} quote${c.quotes === 1 ? "" : "s"}`, "all:quotes"));
  if (c.unpaid_cents > 0) chips.push(chip(`${fmtMoney(c.unpaid_cents / 100)} unpaid`, "all:receipts"));
  if ((c.tickets || 0) > 0) chips.push(chip(`${c.tickets} ticket${c.tickets === 1 ? "" : "s"}`, ""));
  if (c.notes > 0) chips.push(chip(`${c.notes} note${c.notes === 1 ? "" : "s"}`, "all:notes"));
  if (c.docs > 0) chips.push(chip(`${c.docs} doc${c.docs === 1 ? "" : "s"}`, "all:docs"));

  type Sec = { key: string; n: number; rows: Array<{ main: string; meta?: string; warn?: boolean }>; add?: string };
  const secs: Sec[] = [];
  secs.push({
    key: "tasks", n: c.tasks, add: cur.id != null ? "add:task" : undefined, // company scope: no contact to attach
    rows: (s.tasks || []).map((t) => ({ main: t.title, meta: t.due_date ? `due ${fmtDate(t.due_date)}` : t.status })),
  });
  if (data.flags.quotes) secs.push({
    key: "quotes", n: c.quotes, // read-only on the phone (composing is desktop) → no "+"
    rows: (s.quotes || []).map((q) => ({
      main: `${q.ref || "#" + q.id} · ${q.stage || q.status}`,
      meta: [q.total_excl_cents ? fmtMoney(Number(q.total_excl_cents) / 100) : null, q.visitors ? `viewed ${q.visitors}×` : null].filter(Boolean).join(" · ") || undefined,
    })),
  });
  secs.push({
    key: "receipts", n: c.receipts, add: "add:receipt",
    rows: (s.receipts || []).map((r) => ({
      main: [r.vendor_name || "—", r.doc_ref].filter(Boolean).join(" · "),
      meta: [r.amount_total ? fmtMoney(Number(r.amount_total), r.currency || "EUR") : null, r.paid ? "paid" : "unpaid"].filter(Boolean).join(" · "),
      warn: !r.paid,
    })),
  });
  if (data.flags.tickets && (s.tickets || []).length) secs.push({
    key: "tickets", n: c.tickets || 0, // external system — read-only, never a "+"
    rows: (s.tickets || []).map((t) => ({ main: `${t.ref} ${t.subject}`, meta: `${t.stage} · ${fmtDate(t.updated_at)}` })),
  });
  secs.push({
    key: "notes", n: c.notes, add: cur.email ? "add:note" : undefined, // a note needs an attendee email
    rows: (s.notes || []).map((m) => ({ main: m.title, meta: m.meeting_date ? fmtDate(m.meeting_date) : undefined })),
  });
  secs.push({
    key: "docs", n: c.docs, // docs are read-only on the phone → no "+"
    rows: (s.docs || []).map((d) => ({ main: d.name, meta: fmtDate(d.first_seen_at) })),
  });
  const shownSecs = secs.filter((x) => x.rows.length > 0 || x.add || x.n > 0);

  const tlAll = [...(data.timeline?.items ?? []), ...tlMore];

  host.innerHTML =
    webStripHtml() +
    (data.recap || recapBusy
      ? `<div class="c3m-recap">${data.recap ? esc(data.recap.text) : ""}<span class="c3m-recap-tag">AI recap${recapBusy ? " · updating…" : ""}</span></div>`
      : "") +
    (chips.length ? `<div class="c3m-chips">${chips.join("")}</div>` : "") +
    (allZero(c) ? `<div class="c3m-empty">Nothing linked to ${esc(who)} yet — use a section’s + to create the first item.</div>` : "") +
    shownSecs.map((sec) =>
      `<div class="c3m-sec"><div class="c3m-sec-hd">` +
      `<span class="c3m-lab">${esc(SEC_TITLES[sec.key])} <b>${sec.n}</b></span><span class="sp"></span>` +
      (sec.n > sec.rows.length && navAct(sec.key) ? `<button type="button" class="c3m-all" data-act="all:${sec.key}">all →</button>` : "") +
      (sec.add ? `<button type="button" class="c3m-add" data-act="${sec.add}" title="New ${sec.key.replace(/s$/, "")} for ${esc(who)}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></button>` : "") +
      `</div>` +
      sec.rows.map((r) =>
        `<div class="c3m-row${navAct(sec.key) ? " go" : ""}" ${navAct(sec.key) ? `data-act="all:${sec.key}"` : ""}>` +
        `<div class="c3m-main">${esc(r.main)}</div>` +
        (r.meta ? `<div class="c3m-meta${r.warn ? " warn" : ""}">${esc(r.meta)}</div>` : "") +
        `</div>`).join("") +
      `</div>`).join("") +
    crmSecHtml() +
    (tlAll.length
      ? `<div class="c3m-sec"><div class="c3m-sec-hd"><span class="c3m-lab">History</span></div>` +
        groupTl(tlAll).map((g) => {
          const first = g.items[0];
          if (g.items.length === 1) {
            return `<div class="c3m-row"><div class="c3m-tl-hd"><span class="c3m-tl-mod">${esc(MOD_LABELS[first.module] || first.module)}${KIND_SUFFIX[first.kind] ? ` ${KIND_SUFFIX[first.kind]}` : ""}</span><span class="c3m-tl-date">${esc(fmtDate(first.at))}</span></div>` +
              `<div class="c3m-main">${esc(first.title)}</div>` +
              (first.meta || first.actor ? `<div class="c3m-meta">${esc([first.meta, first.actor ? `by ${first.actor}` : null].filter(Boolean).join(" — "))}</div>` : "") +
              `</div>`;
          }
          // Consecutive events of the SAME item collapse into one card — title once, COLLAPSED by
          // default (round 2): tap to expand the per-event lines.
          const open = tlOpenG.has(g.gid);
          return `<div class="c3m-row go" data-act="tlg:${esc(g.gid)}"><div class="c3m-tl-hd"><span class="c3m-tl-mod"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="width:11px;height:11px;vertical-align:-1px;margin-right:3px;${open ? "" : "transform:rotate(-90deg);"}"><path d="M6 9l6 6 6-6"/></svg>${esc(MOD_LABELS[first.module] || first.module)} · ${g.items.length} events</span><span class="c3m-tl-date">${esc(fmtDate(first.at))}</span></div>` +
            `<div class="c3m-main">${esc(first.title)}</div>` +
            (open ? g.items.map((i) =>
              `<div class="c3m-tl-ev"><span class="t">${esc([i.meta || KIND_SUFFIX[i.kind] || i.kind, i.actor ? `by ${i.actor}` : null].filter(Boolean).join(" — "))}</span><span class="d">${esc(fmtDate(i.at))}</span></div>`).join("") : "") +
            `</div>`;
        }).join("") +
        (tlNext ? `<button type="button" class="c3m-all" data-act="more" ${tlBusy ? "disabled" : ""}>${tlBusy ? "Loading…" : "Show more"}</button>` : "") +
        `</div>`
      : "") +
    `<button type="button" class="c3m-brain" data-act="brain"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> Ask Brain about this client</button>` +
    // Deep Scan (pane parity): the full dossier for THIS subject, mailed. Tenant-universal —
    // every lane is capability-gated server-side (no connector → lane absent, never a hole).
    `<button type="button" class="c3m-brain" data-act="deepscan" style="margin-top:8px;opacity:${scanState === "queued" ? "0.75" : "1"}"${scanState === "queued" ? " disabled" : ""}>📡 ${scanState === "queued" ? "Scan started — the dossier lands in your inbox (~2 min)" : "Deep scan this client — full dossier by mail"}</button>` +
    (scanState === "error" ? `<div class="c3m-meta" style="color:var(--bad,#ef4444)">Could not start the scan — please try again.</div>` : "");
}

/** Collapse CONSECUTIVE timeline events of the same item (same module:id key prefix) into one
 *  group — order stays strictly chronological; only the title repetition disappears. */
function groupTl(items: TlItem[]): Array<{ gid: string; items: TlItem[] }> {
  const groups: Array<{ gid: string; items: TlItem[] }> = [];
  for (const i of items) {
    const gid = i.key.split(":").slice(0, 2).join(":");
    const last = groups[groups.length - 1];
    if (last && last.gid === gid) last.items.push(i);
    else groups.push({ gid, items: [i] });
  }
  return groups;
}

const chip = (label: string, act: string): string =>
  `<button type="button" class="c3m-chip" ${act ? `data-act="${act}"` : "disabled"}>${esc(label)}</button>`;
const allZero = (c: Chips): boolean => !c.tasks && !c.receipts && !c.docs && !c.notes && !c.quotes && !(c.tickets || 0);
const navAct = (key: string): boolean => ["tasks", "quotes", "receipts", "notes", "docs"].includes(key);

// ── actions ───────────────────────────────────────────────────────────────────
function onClick(e: Event): void {
  const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
  if (!btn || !cur) return;
  const act = btn.getAttribute("data-act") || "";

  if (act === "more") { if (hostEl) void showMore(hostEl); return; }
  if (act === "webrefresh") { if (hostEl) void refreshWeb(hostEl, true); return; }
  if (act.startsWith("tlg:")) {
    const gid = act.slice(4);
    if (tlOpenG.has(gid)) tlOpenG.delete(gid); else tlOpenG.add(gid);
    if (hostEl) paint(hostEl);
    return;
  }
  // §9.4: "crm" now FETCHES in place; only the explicit "Open in Brain" leaves the fiche.
  if (act === "crm") { if (hostEl) void refreshCrm(hostEl); return; }
  if (act === "crmbrain") {
    const subject = cur.company || cur.name || cur.domain || cur.email || "this client";
    onNavigate?.();
    askConnect(`Give me the Customer 360 for ${subject} from the CRM — open quotes, orders, invoices and recent activity.${cur.email ? ` Contact e-mail: ${cur.email}.` : cur.domain ? ` Company domain: ${cur.domain}.` : ""}`, "crm");
    return;
  }
  if (act === "brain") {
    const subject = cur.name || cur.company || cur.domain || cur.email || "this client";
    const co = cur.name ? (cur.company || "").trim() : "";
    onNavigate?.();
    askConnect(`Give me a complete picture of ${subject}${co ? ` (${co})` : ""} — open tasks, quotes and their engagement, unpaid invoices, tickets, recent notes and documents.${cur.email ? ` Email: ${cur.email}.` : cur.domain ? ` Domain: ${cur.domain}.` : ""} What needs my attention?`, "chat");
    return;
  }
  if (act === "deepscan") {
    // The company leads the subject (the dossier covers the counterparty, not one contact).
    const subject = (cur.company || cur.name || cur.email || cur.domain || "").trim();
    if (!subject || scanState === "queued") return;
    scanState = "queued";
    if (hostEl) paint(hostEl);
    void fetch("/api/connect/meeting-scan", {
      method: "POST", credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ action: "scan", subject }),
    })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); })
      .catch(() => { scanState = "error"; if (hostEl) paint(hostEl); });
    return;
  }
  if (act.startsWith("add:")) {
    const what = act.slice(4);
    onNavigate?.();
    if (what === "task" && cur.id != null) { showScreen("tasks"); void loadTasks(); openTaskFor({ contact_id: Number(cur.id), name: cur.name }); }
    else if (what === "note" && cur.email) { showScreen("minutes"); void loadMinutes(); openMinuteFor({ email: cur.email, name: cur.name }); }
    else if (what === "receipt") { showScreen("receipts"); void loadLedger(); captureReceiptFor({ vendor_name: cur.company || cur.name || undefined, vendor_email: cur.email || undefined, vendor_domain: cur.domain || undefined }); }
    return;
  }
  if (act.startsWith("all:")) {
    const key = act.slice(4);
    const seed = (key === "notes" ? cur.email : key === "receipts" ? (cur.company || cur.name || cur.email) : (cur.name || cur.company || cur.email)) || cur.domain || "";
    onNavigate?.();
    if (key === "tasks") openSearch("tasks", "#tkSearch", loadTasks, seed);
    else if (key === "notes") openSearch("minutes", "#mnSearch", loadMinutes, seed);
    else if (key === "receipts") openSearch("receipts", "#rcSearch", loadLedger, seed);
    else if (key === "docs") openSearch("docs", "#docsSearch", loadDocs, seed);
    else if (key === "quotes") openSearch("quotes", "#quotesSearch", loadQuotes, seed);
  }
}

/** Land on a module screen pre-searched: the loaders paint their snapshot synchronously (the
 *  homesearch idiom) and every search box re-renders on an "input" event. */
function openSearch(screen: Screen, inputSel: string, loader: (force?: boolean) => Promise<void>, q: string): void {
  showScreen(screen);
  void loader();
  const inp = $<HTMLInputElement>(inputSel);
  if (inp) { inp.value = q; inp.dispatchEvent(new Event("input")); }
}

async function showMore(host: HTMLElement): Promise<void> {
  if (!cur || !tlNext || tlBusy) return;
  tlBusy = true; paint(host);
  try {
    const r = await fetch(`/api/c360?${qs(cur)}&mode=timeline&before_at=${encodeURIComponent(tlNext.at)}&before_key=${encodeURIComponent(tlNext.key)}`, { credentials: "include" });
    const j = (await r.json().catch(() => ({}))) as { timeline?: { items: TlItem[]; next: { at: string; key: string } | null } };
    if (r.ok && j.timeline) { tlMore = [...tlMore, ...j.timeline.items]; tlNext = j.timeline.next; }
  } catch { /* keep the button for a retry */ }
  tlBusy = false; paint(host);
}
